import { createI18nSettings } from '@kit/i18n';

/**
 * The default language of the application.
 * This is used as a fallback language when the selected language is not supported.
 *
 */
export const defaultLanguage = process.env.NEXT_PUBLIC_DEFAULT_LOCALE ?? 'de';

/**
 * The list of supported languages.
 * By default, only the default language is supported.
 * Add more languages here if needed.
 */
export const languages: string[] = ['en', 'de', 'fr'];

/**
 * The name of the cookie that stores the selected language.
 */
export const I18N_COOKIE_NAME = 'lang';

/**
 * The default array of Internationalization (i18n) namespaces.
 * These namespaces are commonly used in the application for translation purposes.
 *
 * Add your own namespaces here
 **/
export const defaultI18nNamespaces = [
  'common',
  'auth',
  'account',
  'teams',
  'billing',
  'marketing',
  'rentals',
  'tenants', // Tenant/applicant household management (Issue #458)
  'applicants', // Applicant-specific translations (criteria value display, etc.)
  'rental-case', // Rental case detail pages and tours
  'rental-case-creation', // Rental case creation tour
  'dashboard', // Dashboard and overview pages
  'kanban',
  'criteria',
  'admin',
  'consent', // GDPR consent management (Issue #241) - includes admin.* keys
  'property', // Property management (buildings)
  'property-creation', // Property creation tour
  'applicant-creation', // Applicant creation tour
  'contacts', // Global contacts
  'rentalUnits', // Rental units within properties
  'rental-unit-creation', // Rental unit creation tour
  'media', // Image gallery and media management (Issue #242)
  'rental-cases', // Rental case ↔ unit linking (Issue #242)
  'chatbot', // AI chatbot assistant (Issue #353)
  'entity-memory', // AI Entity Memory UI (Issue #375)
  'search', // Global search functionality (Issue #396)
  'settings', // User and team settings (CardDAV sync, etc.)
  'documents', // Global document management (Issue #401)
  'mcp', // MCP access tokens for external AI integrations
  'tasks', // Task management module
  'reminders', // Reminder management module
  'viewing', // Viewing cancellation and reschedule pages
  'addressbook', // CardDAV address book synchronization (Issue #461)
  'onboarding', // Workspace and user onboarding flows
  'terms', // Terms & Conditions acceptance and management
  'channels', // Multi-channel communication preferences (WhatsApp)
  'notes', // Universal notes system
  'workspace', // Workspace (Notes, Tasks, Reminders)
  'clarifications', // Clarification requests from Applicant Chatbot
  'feedback', // Feedback Hub (bug reports + feature requests)
  'discover', // Public Discovery-Feedback Form nach COMPLETED-Übergang
  'ctools-mgmt', // Chatbot tool output: entity/property/rental management (Pack C)
  'ctools-applicant', // Chatbot tool output: applicant/communication (Pack C)
  'ctools-viewing', // Chatbot tool output: viewing/scheduling/uploads (Pack C)
  'ctools-data', // Chatbot tool output: read/search/locari/misc (Pack C)
  'ctools-common', // Redigierte Fehlertexte fuer den {{error}}-Slot aller ctools-Rahmen (F-1)
  'self-disclosure', // Mieterselbstauskunft tool save/claim flows (#601)
  'email-preferences', // Public token-based email preference center (Spec §5)
  'newsletter', // Public newsletter DOI confirm/unsubscribe pages (Newsletter-Basis N-3)
  'gdprRequests', // GDPR Art. 15 access-request fulfilment (DSGVO-Auskunft-Spec PR 1, §4.5)
  /*
   * Kalender-Abo (Befund 414, Blatt W · W10). Eigener Namensraum, NICHT
   * `addressbook` mitbenutzt: der trägt CardDAV, und zwei Anbindungen in einer
   * Sprachdatei erzeugen beim nächsten Aufräumen genau die Frage, welche
   * Schlüssel wozu gehören.
   *
   * 🔴 Diese Liste ist handgepflegt. Ohne den Eintrag lädt der Namensraum
   * nicht, und `<Trans i18nKey="kalender-abo:…">` rendert ROHE SCHLÜSSEL —
   * genau die Klasse, gegen die `check:i18n-raw-keys` steht.
   */
  'kalender-abo',
  /*
   * „Locari fragen" (Spec 2026-09-24 § 3.6, Blatt AC). Schrittlabels,
   * Spalten, Grenzen und Fehler des Ereignisstroms von /api/fragen und die
   * Beschriftungen des Tabellen-Exports.
   */
  'fragen',
];

/**
 * Get the i18n settings for the given language and namespaces.
 * If the language is not supported, it will fall back to the default language.
 * @param language
 * @param ns
 */
export function getI18nSettings(
  language: string | undefined,
  ns: string | string[] = defaultI18nNamespaces,
) {
  let lng = language ?? defaultLanguage;

  if (!languages.includes(lng)) {
    console.warn(
      `Language "${lng}" is not supported. Falling back to "${defaultLanguage}"`,
    );

    lng = defaultLanguage;
  }

  return createI18nSettings({
    language: lng,
    namespaces: ns,
    languages,
  });
}
